// Mounted once beside the app (main.tsx). Shows the floating preview switch on phones and the stage while this device
// has a preview on and the battle's look is not locked; staff pages carry their own switch, so it stays away there.
// On the stage (an OBS browser source) it appears when the mouse moves and hides again, so a stream never shows it.
import { useEffect, useState } from 'react';
import { isStagePath, isStaffPath } from './look';
import { useLook, useLookStore } from './store';
import { LookSwitch } from './LookSwitch';

/** True while the pointer has moved in the last `ms` (stage only). */
function useAwake(active: boolean, ms = 3000): boolean {
  const [awake, setAwake] = useState(false);
  useEffect(() => {
    if (!active) return;
    let t = 0;
    const poke = () => { setAwake(true); window.clearTimeout(t); t = window.setTimeout(() => setAwake(false), ms); };
    const events = ['pointermove', 'pointerdown', 'keydown'] as const;
    events.forEach((e) => window.addEventListener(e, poke, { passive: true }));
    return () => { events.forEach((e) => window.removeEventListener(e, poke)); window.clearTimeout(t); };
  }, [active, ms]);
  return awake;
}

export function LookLayer() {
  const { previewing, locked } = useLook();
  const staffSwitches = useLookStore((s) => s.staff);
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  const show = previewing && !locked && staffSwitches === 0 && !isStaffPath(path);
  const stage = isStagePath(path);
  const awake = useAwake(show && stage);
  if (!show) return null;
  const hidden = stage && !awake;
  return (
    <div className={`rd-look-float fixed z-[70] max-w-[calc(100vw-1rem)] rounded-[14px] border border-line bg-surface/95 p-0.5 shadow-pop backdrop-blur-sm transition-opacity duration-200 ${hidden ? 'pointer-events-none opacity-0' : 'opacity-100'}`}
      style={{ left: 'max(0.5rem, env(safe-area-inset-left))', top: 'max(0.5rem, env(safe-area-inset-top))' }} aria-hidden={hidden || undefined}>
      <LookSwitch variant="preview" />
    </div>
  );
}
